import { Injectable, signal, computed, inject } from "@angular/core";
import { take } from "rxjs/operators";
import { CurrentUserApiService } from "../current-user/current-user-api.service";
import { UserCapabilityGrant } from "@app/areas/admin-users/services/admin-users.service";

@Injectable({
  providedIn: "root"
})
export class UserCapabilitiesService {
  private readonly currentUserApi = inject(CurrentUserApiService);

  private readonly capabilities = signal<UserCapabilityGrant[] | null>(null);

  /**
   * Public read-only signal representing the loaded capability grants.
   */
  readonly userCapabilities$ = computed(() => this.capabilities());

  /**
   * Public read-only signal indicating if the capabilities have loaded.
   */
  readonly loaded$ = computed(() => this.capabilities() !== null);

  constructor() {
    this.loadCapabilities();
  }

  /**
   * Refreshes the logged in user capabilities list.
   */
  loadCapabilities(): void {
    this.currentUserApi.getCapabilities()
      .pipe(take(1))
      .subscribe({
        next: (grants) => {
          this.capabilities.set(grants ?? []);
        },
        error: () => {
          // Gracefully fallback to empty array on failure so UI is unblocked
          this.capabilities.set([]);
        }
      });
  }

  /**
   * Returns a fine-grained, high-performance computed Signal checking if the logged-in user
   * has a specific capability. This is reactive and will update immediately when capabilities load.
   *
   * @param key Stable capability key (e.g. "prescription_builder:create")
   */
  hasCapabilitySignal(key: string) {
    return computed(() => {
      const grants = this.capabilities();
      if (grants === null) {
        // Still loading: return false so we hide the element in the interim
        return false;
      }

      const grant = grants.find(g => g.capabilityKey === key);
      return grant ? grant.status === "granted" : false;
    });
  }
}
